// hooks/useCycleData.js
import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import apiService from '../../services/api';
import { ENDPOINTS } from '../../services/endpoints';

const cycleDataQueryDefaults = {
  staleTime: 1000 * 60 * 5,     // 5 minutes
  gcTime: 1000 * 60 * 30,       // 30 minutes cache
  retry: 2,
  retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
};

export const useCycleData = (viewMode = 'current', selectedCycleMonth = null) => {
  
  const flattenCycleData = useCallback((responseData) => {
    if (!responseData) {
      return { clients: [], metadata: {} };
    }

    const cycleData = responseData.data || responseData;
    const clients = [];
    const metadata = {
      view_mode: cycleData.view_mode,
      period_label: cycleData.period_label,
      analysis_date: cycleData.analysis_date,
      cycle_month: cycleData.cycle_month
    };
    
    // Process retainer billing
    if (cycleData.retainer_billing && Array.isArray(cycleData.retainer_billing)) {
      cycleData.retainer_billing.forEach((group) => {
        if (group.clients && Array.isArray(group.clients)) {
          group.clients.forEach(client => {
            clients.push({
              client_name: client.customer,
              customer: client.customer,
              parent_customer: client.parent_customer,
              billing_cycle: group.billing_cycle,
              client_type: 'retainer',
              client_hours: client.hours_logged,
              billable_incurred: client.time_cost,
              fees_collected: client.monthly_fee,
              overservice_amount: client.overservice_amount,
              completion_percentage: client.completion_percentage,
              employee_count: client.employee_count,
              cycle_start: client.cycle_start,
              cycle_end: client.cycle_end,
              last_invoice_date: client.last_invoice_date,
              status: client.status
            });
          });
        }
      });
    }

    // Process project billing
    if (cycleData.project_billing?.clients && Array.isArray(cycleData.project_billing.clients)) {
      cycleData.project_billing.clients.forEach(client => {
        clients.push({
          client_name: client.project_name || client.customer,
          customer: client.customer,
          parent_customer: client.parent_customer,
          project_name: client.project_name,
          billing_cycle: 'project',
          client_type: 'project',
          client_hours: client.hours_logged,
          billable_incurred: client.time_cost,
          fees_collected: client.total_fees,
          overservice_amount: client.overservice_amount,
          completion_percentage: client.completion_percentage,
          employee_count: client.employee_count,
          project_start_date: client.project_start_date,
          last_activity_date: client.last_activity_date,
          invoice_count: client.invoice_count,
          status: client.status
        });
      });
    }

    return { clients, metadata };
  }, []);

  const {
    data: cycleData,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['cycleData', viewMode, selectedCycleMonth],
    queryFn: async () => {
      const startTime = performance.now();
      
      const params = {
        view_mode: viewMode,
        ...(viewMode === 'custom' && selectedCycleMonth && { cycle_month: selectedCycleMonth })
      };
      
      const response = await apiService.get(ENDPOINTS.TIME_OVERSERVICE_COMPARISON, params);
            
      return response;
    },
    ...cycleDataQueryDefaults,
  });

  const overservicingData = useMemo(() => {
    if (!cycleData) return null;
    
    let actualData = null;
    if (cycleData.data?.data) {
      actualData = cycleData.data.data;
    } else if (cycleData.data) {
      actualData = cycleData.data;
    } else if (cycleData.retainer_billing || cycleData.project_billing) {
      actualData = cycleData;
    }
    
    if (!actualData) return null;
    
    const { clients, metadata } = flattenCycleData(actualData);
    const summaryData = actualData.summary || {};
    
    return {
      client_summaries: clients,
      summary_stats: {
        total_billable_incurred: summaryData.total_time_cost || 0,
        total_fees: summaryData.total_fees || 0,
        net_overservice_underservice: summaryData.total_overservice || 0,
        total_clients: summaryData.total_clients || 0,
        clients_with_overservice: summaryData.clients_with_overservice || 0,
        clients_with_underservice: summaryData.clients_with_underservice || 0
      },
      cycle_metadata: metadata
    };
  }, [cycleData, flattenCycleData]);

  return {
    // Data
    overservicingData,
    cycleMetadata: overservicingData?.cycle_metadata,
    
    // Loading states
    loading: isLoading,
    refreshing: isFetching,
    
    // Error handling
    error: isError ? (error?.message || 'Failed to load cycle data') : null,
    isError,
    
    // Status
    initialized: !!cycleData,
    
    // Actions
    refetch,
  };
};